import React from "react";
import { useFormikContext } from "formik";
import { useAlert } from "react-alert";
import { Button, ButtonGroup } from "../../../components/ui";
import { useRequestContext } from "../../../requests/hooks/useRequest.js";

// data-btn per option. add_main can appear twice (Record a Sale's "Source
// Product" + "Add Listing"); they differ by the tab the new slot opens on.
const btnId = ({ option, newSlotSource }) =>
  option === "finish"
    ? "request-confirm"
    : option === "add"
      ? "request-add-px"
      : newSlotSource
        ? `request-add-${newSlotSource}`
        : "request-add-main";

export function ConfirmButtons({ buttons, handleSubmit, setChoice }) {
  const { setLastEvent } = useRequestContext();
  const { validateForm, setTouched } = useFormikContext();
  const alert = useAlert();

  const handleClick = async (button) => {
    setChoice(button.option, button.newSlotSource ?? null);
    if (button.label.toLowerCase().includes("continue")) {
      setLastEvent("continue");
    } else {
      setLastEvent(button.option);
    }

    // Validate FIRST so a silent Yup rejection doesn't make the
    // button look like a no-op. Formik's handleSubmit also runs the
    // schema, but it only flips its internal errors state without
    // surfacing anything — Alex reported a prod request with year=null
    // where every CONFIRM REQUEST button did nothing and no API
    // request fired. Now we toast the failing fields and mark them
    // touched so the in-form error styling can show too.
    const errors = await validateForm();
    const invalidFields = Object.keys(errors);
    if (invalidFields.length > 0) {
      const touchedMap = invalidFields.reduce(
        (acc, k) => ({ ...acc, [k]: true }),
        {}
      );
      setTouched(touchedMap, false);
      // react-alert-template-basic collapses whitespace, so join with
      // " · " instead of newlines. The Yup messages on this form
      // already mention the field by name (e.g. "Please insert a valid
      // year"), so the toast stays readable without a label prefix.
      const summary = invalidFields.map((f) => errors[f]).join(" · ");
      alert.show(`Please fix before continuing — ${summary}`);
      return;
    }

    handleSubmit(button.option);
  };

  return (
    // `action-buttons` keeps the flow-wide action box size (request-v2.scss).
    // The row's one primary is the step's own commit (finish); the adds are secondary.
    <ButtonGroup align="end" className="action-buttons">
      {buttons.map((button) => (
        <Button
          key={button.label}
          variant={button.option === "finish" ? "primary" : "secondary"}
          size="lg"
          data-btn={btnId(button)}
          disabled={button.disabled}
          onClick={() => handleClick(button)}
        >
          {button.label}
        </Button>
      ))}
    </ButtonGroup>
  );
}
